import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, History, RefreshCw, Shield } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { resolveIsAdmin } from '../../utils/adminAccess';
import PerformanceChart from '../../pages/dashboard/components/PerformanceCharts';
import { ADMIN_TRADE_HISTORY_START } from '../../utils/adminTradeHistoryWindow';
import { ADMIN_TRACKED_LABEL } from '../../utils/adminTradeSymbols';
import { jtncDailyPnl, jtncTradeLedger } from '../../utils/qfaaTradierPnl';
import useJtncPnlInputs from './useJtncPnlInputs';
import {
  isRulesDeployPending,
  jtncLedgerTitle,
  jtncUnavailableHint,
  jtncUnavailableTitle,
} from './jtncLedgerText';
import { formatMoney, pnlClass } from './AdminTradierHistoryPanel';
import useAdminTradierHistory from './useAdminTradierHistory';
import './AdminClientPerformance.css';

const PERIODS = [
  { id: '1W', label: '1W' },
  { id: '1M', label: '1M' },
  { id: '1Y', label: '1Y' },
  { id: 'All', label: 'All' },
];

const AdminClientPerformance = () => {
  const { user, userProfile, loading: authLoading } = useAuthContext();
  const [searchParams] = useSearchParams();
  const userId = (searchParams.get('userId') || '').trim();
  const emailHint = (searchParams.get('email') || '').trim();

  const [operatorClaim, setOperatorClaim] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(Boolean(userId));
  const [profileError, setProfileError] = useState(null);
  const [period, setPeriod] = useState('All');

  useEffect(() => {
    const id = 'q-admin-fonts';
    if (document.getElementById(id)) return undefined;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600&display=swap';
    document.head.appendChild(link);
    return undefined;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setOperatorClaim(false);
      return undefined;
    }
    user
      .getIdTokenResult()
      .then((r) => {
        if (!cancelled) setOperatorClaim(r.claims?.qfaaOperator === true);
      })
      .catch(() => {
        if (!cancelled) setOperatorClaim(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const gateResolved = !authLoading && operatorClaim !== null;
  const isAdmin = resolveIsAdmin({ user, userProfile, operatorClaim });

  const tradierHistory = useAdminTradierHistory(isAdmin && operatorClaim === true && userId ? userId : null);
  const tradierEvents = tradierHistory.data?.events;
  const jtncInputs = useJtncPnlInputs(
    isAdmin && operatorClaim === true && userId ? userId : null,
    tradierHistory.data?.accountId || null
  );
  const ledger = useMemo(
    () => (tradierEvents && !jtncInputs.loading && !jtncInputs.error ? jtncTradeLedger(tradierEvents, jtncInputs) : null),
    [tradierEvents, jtncInputs]
  );
  const dailyPnl = useMemo(() => {
    if (ledger) return jtncDailyPnl(ledger);
    return tradierHistory.error || jtncInputs.error ? [] : null;
  }, [ledger, tradierHistory.error, jtncInputs.error]);
  const totalPnl = ledger ? ledger.total : null;
  const since = ADMIN_TRADE_HISTORY_START.toLocaleString('en-US', { month: 'short', year: 'numeric' });

  useEffect(() => {
    let cancelled = false;
    if (!userId || !db) {
      setProfile(null);
      setProfileLoading(false);
      setProfileError(userId ? null : 'Missing userId');
      return undefined;
    }

    setProfileLoading(true);
    setProfileError(null);
    getDoc(doc(db, 'users', userId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setProfile(null);
          setProfileError('User profile not found');
        } else {
          const data = snap.data() || {};
          setProfile({
            id: snap.id,
            email: data.email || emailHint || '',
            name: data.fullName || data.displayName || data.name || data.username || data.email || snap.id,
            status: data.status || '',
            tier: data.tier || '',
          });
          setProfileError(null);
        }
        setProfileLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Admin client profile load failed:', err);
        setProfileError(err?.message || 'Failed to load user');
        setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, emailHint]);

  const title = useMemo(() => {
    if (profile?.name) return profile.name;
    if (emailHint) return emailHint;
    return userId || 'Client';
  }, [profile, emailHint, userId]);

  if (!gateResolved) {
    return (
      <div className="acp-page acp-center">
        <RefreshCw className="h-5 w-5 animate-spin" />
        Checking access…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="acp-page acp-center">
        <div className="acp-denied">
          <Shield className="h-5 w-5" />
          Access Denied: Admin privileges required
        </div>
      </div>
    );
  }

  return (
    <div className="acp-page">
      <div className="acp-shell">
        <nav className="acp-nav">
          <Link to="/admin" className="acp-nav-link">
            <ArrowLeft />
            Admin Dashboard
          </Link>
          <span className="acp-nav-sep" aria-hidden="true" />
          <Link
            to={userId ? `/admin/client-trade-history?client=${encodeURIComponent(userId)}` : '/admin/client-trade-history'}
            className="acp-nav-link muted"
          >
            <History />
            Trade history
          </Link>
        </nav>

        <header className="acp-header">
          <div className="acp-identity">
            <p className="acp-eyebrow">Client performance</p>
            <h1>{profileLoading ? 'Loading client…' : title}</h1>
            <div className="acp-meta">
              {(profile?.email || emailHint) && (
                <span className="acp-meta-email">{profile?.email || emailHint}</span>
              )}
              {profile?.status && <span className="acp-chip">{profile.status}</span>}
              {profile?.tier && <span className="acp-chip dim">{profile.tier}</span>}
            </div>
            {userId && <code className="acp-uid">{userId}</code>}
          </div>

          <div className="acp-period" role="tablist" aria-label="Chart period">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={period === p.id}
                className={period === p.id ? 'active' : ''}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </header>

        {profileError && <div className="acp-error">{profileError}</div>}

        {!userId ? (
          <div className="acp-empty">Open this page from Admin Dashboard with a client user id.</div>
        ) : (
          <section className="acp-chart-panel">
            <div className="acp-chart-head">
              <div>
                <h2>JTNC QFAA Trade P&amp;L (cumulative)</h2>
                <p>
                  From Tradier · {ADMIN_TRACKED_LABEL} JTNC trades since {since}, after commissions, plus credits/bonuses, Pro subscriptions and reimbursements · Echo, test and personal trades left out · drag to pan
                </p>
              </div>
              <div className="text-right" title={jtncLedgerTitle(ledger, since)}>
                <div className={`text-lg font-semibold tabular-nums ${totalPnl == null ? 'text-gray-400' : pnlClass(totalPnl)}`}>
                  {totalPnl == null && (tradierHistory.loading || jtncInputs.loading) ? '…' : formatMoney(totalPnl)}
                </div>
                <div className="text-[11px] text-gray-400">Total since {since}</div>
                {jtncInputs.error && (
                  <div className="text-[10px] text-amber-400/80" title={jtncUnavailableTitle(jtncInputs.error)}>
                    {jtncUnavailableHint(jtncInputs.error)}
                  </div>
                )}
              </div>
            </div>
            {tradierHistory.error && (
              <div className="acp-error">Tradier history unavailable: {tradierHistory.error}</div>
            )}
            {jtncInputs.error && !isRulesDeployPending(jtncInputs.error) && (
              <div className="acp-error">Desk trade list unavailable, so JTNC P&amp;L is not shown: {jtncInputs.error}</div>
            )}
            <div className="acp-chart-body">
              <PerformanceChart
                key={`${userId}-${period}`}
                forUserId={userId}
                presentation="admin"
                symbol={['ALL']}
                period={period}
                viewMode="cumulative"
                externalDaily={dailyPnl}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default AdminClientPerformance;
